// Shared public marketing-content access for surfaces that need CMS copy but don't own a full
// retryable cms lifecycle (the sitewide footer, /contact, /solutions/*). ONE memoized fetch per
// session shared by every consumer — a page that also fetches /public/marketing itself (Landing,
// /pricing, /aira) keeps its own state and this cache simply de-duplicates the footer's read.
// A failed fetch resolves null (consumers keep their byte-mirrored fallback copy) and clears the
// memo so a later mount retries.
import { useEffect, useState } from "react";
import api from "./api";

let inflight = null;

// TECH-2 — prerender CMS bootstrap. scripts/prerender.mjs inlines the prerender-time
// /public/marketing payload into each CMS-fed snapshot as
//   <script id="vp360-cms-bootstrap">window.__VP360_MARKETING_BOOTSTRAP__={...}</script>
// so hydration renders the SAME published copy the snapshot was captured with (no flash of
// code-fallback text, no recoverable hydration mismatch). Consumed EXACTLY ONCE: the first
// getMarketingContent() call seeds the session memo from it and deletes the global, so a later
// cache reset (test seam) or SPA navigation falls back to the normal network fetch.
function consumeBootstrapPayload() {
  if (typeof window === "undefined") return null;
  const payload = window.__VP360_MARKETING_BOOTSTRAP__;
  try { delete window.__VP360_MARKETING_BOOTSTRAP__; } catch { /* non-configurable — ignore */ }
  return payload && typeof payload === "object" ? payload : null;
}

// Synchronous access to the snapshot's embedded payload, for seeding INITIAL React state.
// getMarketingContent() resolves through a Promise — a microtask AFTER the first hydrated
// render — so a page whose first render depends on the CMS briefly painted the code-fallback
// copy over the snapshot's published copy and the layout JUMPED when the promise landed
// (measured: the /pricing fixed-plans grid shifted ~28px; Lighthouse desktop CLS 0.105 against
// the 0.1 budget, the round-8 CI failure). Seeding useState from this makes the first hydrated
// render IDENTICAL to the snapshot; the shared promise memo is primed so every later consumer
// (SiteFooter etc.) stays coherent with the consume-once contract.
let bootstrapMemo = null;

export function getInitialMarketingContent() {
  if (!bootstrapMemo) {
    const payload = consumeBootstrapPayload();
    if (payload) {
      bootstrapMemo = payload;
      if (!inflight) inflight = Promise.resolve(payload);
    }
  }
  return bootstrapMemo;
}

export function getMarketingContent() {
  if (!inflight) {
    const bootstrap = getInitialMarketingContent();
    if (bootstrap) {
      inflight = Promise.resolve(bootstrap);
      return inflight;
    }
    inflight = api
      .get("/public/marketing")
      .then(({ data }) => data)
      .catch(() => {
        inflight = null;
        return null;
      });
  }
  return inflight;
}

export function useMarketingContent() {
  const [cms, setCms] = useState(getInitialMarketingContent);
  useEffect(() => {
    let mounted = true;
    getMarketingContent().then((data) => {
      if (mounted && data) setCms(data);
    });
    return () => {
      mounted = false;
    };
  }, []);
  return cms;
}

// test seam: reset the memo between jest cases
export function __resetMarketingContentCache() {
  bootstrapMemo = null;
  inflight = null;
}
